import React, { useState, useRef, useEffect } from 'react';
import { cn } from '@h2/ui';
import Icon from '../ui/icon';
import { useChatStore } from '../../stores/useChatStore';

const ChatPanel: React.FC = () => {
  const {
    isChatOpen,
    activeRoomId,
    chatRooms,
    messages,
    closeChat,
    setActiveRoom,
    sendMessage,
    markAsRead
  } = useChatStore();

  const [messageInput, setMessageInput] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    if (activeRoomId) {
      scrollToBottom();
    }
  }, [messages, activeRoomId]);

  const handleSendMessage = () => {
    if (messageInput.trim() && activeRoomId) {
      sendMessage(activeRoomId, messageInput.trim());
      setMessageInput('');
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const formatTime = (date: Date) => {
    return date.toLocaleTimeString('ko-KR', { 
      hour: '2-digit', 
      minute: '2-digit',
      hour12: false 
    });
  };

  const formatRelativeTime = (date: Date) => {
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) return '방금 전';
    if (diffMins < 60) return `${diffMins}분 전`;
    if (diffHours < 24) return `${diffHours}시간 전`;
    if (diffDays < 7) return `${diffDays}일 전`;
    return date.toLocaleDateString('ko-KR');
  };

  const handleRoomClick = (roomId: string) => {
    setActiveRoom(roomId);
    markAsRead(roomId);
  };

  const activeRoom = chatRooms.find(room => room.id === activeRoomId);
  const roomMessages = activeRoomId ? messages[activeRoomId] || [] : [];

  if (!isChatOpen) return null;

  return (
    <div className="fixed bottom-4 right-4 w-96 h-[600px] bg-white dark:bg-gray-800 rounded-lg shadow-2xl border border-gray-200 dark:border-gray-700 flex flex-col overflow-hidden z-50">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700">
        <div className="flex items-center space-x-2">
          <Icon name="MessageCircle" size={20} className="text-blue-600 dark:text-blue-400" />
          <h3 className="font-semibold text-gray-900 dark:text-gray-100">
            {activeRoom ? activeRoom.name : '채팅'}
          </h3>
        </div>
        <div className="flex items-center space-x-1">
          {activeRoom && (
            <button
              onClick={() => setActiveRoom(null)}
              className="p-1 hover:bg-gray-200 dark:hover:bg-gray-600 rounded transition-colors"
              title="채팅방 목록으로"
            >
              <Icon name="ChevronLeft" size={16} className="text-gray-600 dark:text-gray-400" />
            </button>
          )}
          <button
            onClick={closeChat}
            className="p-1 hover:bg-gray-200 dark:hover:bg-gray-600 rounded transition-colors"
            title="채팅 닫기"
          >
            <Icon name="X" size={16} className="text-gray-600 dark:text-gray-400" />
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-hidden">
        {!activeRoom ? (
          /* Chat Room List */
          <div className="h-full overflow-y-auto">
            <div className="p-3">
              <div className="text-sm text-gray-500 dark:text-gray-400 mb-3">채팅방 목록</div>
              {chatRooms.map((room) => (
                <button
                  key={room.id}
                  onClick={() => handleRoomClick(room.id)}
                  className="w-full p-3 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-lg transition-colors text-left mb-2"
                >
                  <div className="flex items-center space-x-3">
                    <div className="relative">
                      <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center">
                        <span className="text-white text-sm font-medium">
                          {room.name.charAt(0)}
                        </span>
                      </div>
                      {room.isOnline && (
                        <div className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-green-500 rounded-full border-2 border-white"></div>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between">
                        <div className="font-medium text-gray-900 dark:text-gray-100 truncate">
                          {room.name}
                        </div>
                        {room.lastMessage && (
                          <div className="text-xs text-gray-500 dark:text-gray-400">
                            {formatRelativeTime(room.lastMessage.timestamp)}
                          </div>
                        )}
                      </div>
                      <div className="flex items-center justify-between">
                        <div className="text-sm text-gray-600 dark:text-gray-300 truncate">
                          {room.lastMessage?.content || '새 채팅'}
                        </div>
                        {room.unreadCount > 0 && (
                          <div className="bg-red-500 text-white text-xs rounded-full px-2 py-0.5 min-w-[20px] text-center">
                            {room.unreadCount}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        ) : (
          /* Chat Messages */
          <div className="h-full flex flex-col">
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              {roomMessages.map((message) => (
                <div
                  key={message.id}
                  className={cn(
                    'flex',
                    message.senderId === 'me' ? 'justify-end' : 'justify-start'
                  )}
                >
                  <div
                    className={cn(
                      'max-w-[70%] px-3 py-2 rounded-lg',
                      message.senderId === 'me'
                        ? 'bg-blue-600 text-white'
                        : 'bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-gray-100'
                    )}
                  >
                    <div className="text-sm">{message.content}</div>
                    <div
                      className={cn(
                        'text-xs mt-1',
                        message.senderId === 'me'
                          ? 'text-blue-100'
                          : 'text-gray-500 dark:text-gray-400'
                      )}
                    >
                      {formatTime(message.timestamp)}
                    </div>
                  </div>
                </div>
              ))}
              <div ref={messagesEndRef} />
            </div>

            {/* Message Input */}
            <div className="p-4 border-t border-gray-200 dark:border-gray-700">
              <div className="relative flex items-center">
                <textarea
                  value={messageInput}
                  onChange={(e) => setMessageInput(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder="메시지를 입력하세요..."
                  className="w-full px-4 py-3 pr-14 border border-gray-300 dark:border-gray-600 rounded-full resize-none focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm leading-5 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400"
                  rows={1}
                  style={{ minHeight: '44px', maxHeight: '100px' }}
                />
                <button
                  onClick={handleSendMessage}
                  disabled={!messageInput.trim()}
                  className={cn(
                    'absolute right-3 top-1/2 transform -translate-y-1/2 w-8 h-8 rounded-full flex items-center justify-center transition-all duration-200',
                    messageInput.trim()
                      ? 'bg-blue-600 text-white hover:bg-blue-700 scale-100'
                      : 'bg-gray-300 text-gray-500 cursor-not-allowed scale-95'
                  )}
                >
                  <Icon name="Send" size={16} />
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ChatPanel;
